// src/realtime/client.ts
import { ApiError } from "@/api/errors";

import type { CloseInfo, Frame, RealtimeEvent, RealtimeStatus, Transport } from "./types";

export type AuthLostReason = "session_expired" | "revoked" | "unauthorized";

export type RealtimeOptions = {
  transport: Transport;
  getTicket: () => Promise<string>;
  onEvent: (e: RealtimeEvent) => void;
  onStatus?: (s: RealtimeStatus) => void;
  onFrame?: (f: Frame) => void;
  /** يُستدعى بعد كل ready؛ first=false يعني إعادة اتصال (اجلب الحالة لسد أي فجوة). */
  onReady?: (first: boolean) => void;
  onAuthLost?: (reason: AuthLostReason) => void;
  random?: () => number;
};

const BASE_DELAY = 500;
const MAX_DELAY = 15_000;

/**
 * عميل الوقت الحقيقي: تذكرة جديدة لكل اتصال، إعادة اتصال بتراجع أسي مع عشوائية،
 * ومراقب صمت (watchdog) يقطع الاتصال الميت. لا replay للأحداث الفائتة: بعد كل
 * إعادة اتصال الواجهة تجلب حالتها (onReady(false)).
 */
export class RealtimeClient {
  private status: RealtimeStatus = "idle";
  private attempt = 0;
  private connection: { close: () => void } | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private watchdog: ReturnType<typeof setTimeout> | undefined;
  private heartbeat = 20;
  private everReady = false;
  private stopped = true;
  private generation = 0;

  constructor(private opts: RealtimeOptions) {}

  start() {
    if (!this.stopped) return;
    this.stopped = false;
    void this.connect();
  }

  stop() {
    this.stopped = true;
    this.generation++;
    clearTimeout(this.retryTimer);
    clearTimeout(this.watchdog);
    this.connection?.close();
    this.connection = null;
    this.setStatus("stopped");
  }

  getStatus = () => this.status;

  private setStatus(s: RealtimeStatus) {
    if (this.status === s) return;
    this.status = s;
    this.opts.onStatus?.(s);
  }

  private async connect() {
    const gen = ++this.generation;
    this.setStatus(this.everReady ? "reconnecting" : "connecting");
    let ticket: string;
    try {
      ticket = await this.opts.getTicket();
    } catch (err) {
      if (gen !== this.generation) return;
      if (err instanceof ApiError && err.status === 401) return this.authLost("unauthorized");
      return this.scheduleRetry();
    }
    if (gen !== this.generation || this.stopped) return;

    this.connection = this.opts.transport.open(ticket, {
      onFrame: (f) => gen === this.generation && this.handleFrame(f),
      onClose: (info) => gen === this.generation && this.handleClose(info),
    });
  }

  private handleFrame(frame: Frame) {
    this.armWatchdog();
    this.opts.onFrame?.(frame);
    switch (frame.kind) {
      case "ready": {
        this.heartbeat = frame.data.heartbeat_seconds || 20;
        this.attempt = 0;
        const first = !this.everReady;
        this.everReady = true;
        this.setStatus("live");
        this.armWatchdog();
        this.opts.onReady?.(first);
        return;
      }
      case "event":
        return this.opts.onEvent(frame.data);
      case "session_expired":
      case "revoked":
        return this.authLost(frame.kind);
      case "reset":
      case "server_shutdown":
        return this.reconnect(0);
      case "error":
        return this.fatal();
      default:
    }
  }

  private handleClose(info: CloseInfo) {
    if (info.status === 401 || info.code === 4401) return this.authLost("unauthorized");
    if (info.status === 429 || info.code === 4429) return this.fatal();
    if (info.code === 4408) return this.authLost("session_expired");
    if (info.code === 4403) return this.authLost("revoked");
    this.reconnect(this.attempt);
  }

  private authLost(reason: AuthLostReason) {
    this.stop();
    this.opts.onAuthLost?.(reason);
  }

  private fatal() {
    this.stop(); // تجاوز حد الاتصالات: لا فائدة من إعادة المحاولة فوراً
  }

  private reconnect(attempt: number) {
    this.connection?.close();
    this.connection = null;
    clearTimeout(this.watchdog);
    this.attempt = attempt;
    this.scheduleRetry();
  }

  private scheduleRetry() {
    if (this.stopped) return;
    this.setStatus(this.everReady ? "reconnecting" : "connecting");
    const rnd = (this.opts.random ?? Math.random)();
    const delay = Math.min(MAX_DELAY, BASE_DELAY * 2 ** this.attempt) * (0.5 + rnd / 2);
    this.attempt += 1;
    clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => void this.connect(), delay);
  }

  /** لو ما وصلش أي بايت (حتى heartbeat) لمدة 2.5× الفاصل، الاتصال ميت: أعد الاتصال. */
  private armWatchdog() {
    clearTimeout(this.watchdog);
    this.watchdog = setTimeout(() => this.reconnect(0), this.heartbeat * 2500);
  }
}
